import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {Aangifte, BokkzIbDataService, IncomeTaxEntity, LoonBuitenland,} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {debounceTime, filter, map, Observable, pairwise, shareReplay, startWith, Subscription, take} from 'rxjs';
import {optionsLandcodesZonderNLDBLDGDUI} from '../../../gedeelde-componenten/gedeelde-componenten.component';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDialogModule} from '@snelstart/ui-dialog';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

type LoonFormGroup = FormGroup<{
  loon: OmschrijvingEnBedragFormGroup;
  loonheffing: FormControl<number | null>;
  loonsoort: FormControl<string | null>;
  landCode: FormControl<string | null>;
  loonUitTegenwoordigeBuitenlandseDienstbetrekking: FormControl<boolean | null>;
  belastDeelLoonInNederland: FormControl<number | null>;
  buitenlandseLoonheffing: FormControl<number | null>;
  inkomenZitInNederlandseGrondslag: FormControl<boolean | null>;
  pasVerrekenMethodeToe: FormControl<boolean | null>;
  afdrachtZVWinNLmoetnog: FormControl<boolean | null>;
  benutAlgemeneCompensatieregeling: FormControl<boolean | null>;
  benutBijzondereCompensatieregeling: FormControl<boolean | null>;
  compensatieregelingDuitsland: FormControl<boolean | null>;
  veranderingVanWerkgever: FormControl<boolean | null>;
  inBelgieOfDuitslandIngehoudenBelasting?: FormControl<number | null>;
}>;

type OmschrijvingEnBedragFormGroup = FormGroup<{
  omschrijving: FormControl<string | null>;
  bedrag: FormControl<number | null>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-loon-buitenland',
  templateUrl: './loon-buitenland.component.html',
  styleUrls: ['./loon-buitenland.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiDialogModule,
    SharedUiDetailsModule,
    CommonModule
  ]
})
export class LoonBuitenlandComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public dialogVisible = false;
  public index = 0;
  public isPartner = false;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja'},
    {value: false, label: 'Nee'}
  ];

  public optionsJaNeeSter: SelectFormControlItem<boolean>[] = [
    {value: true, label: 'Ja*'},
    {value: false, label: 'Nee'}
  ];

  public optionsTegenwoordig: SelectFormControlItem<boolean>[] = [
    {value: false, label: 'Vroeger (pensioen)'},
    {value: true, label: 'Tegenwoordig (werk)'},
  ];

  public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesZonderNLDBLDGDUI;

  public formGroup = new FormGroup({
    loonUitBuitenland: new FormArray<LoonFormGroup>([]),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(entity => {
        if (partner) {
          this.handleSwitch(entity, true);
        } else {
          this.handleSwitch(entity, false);
        }
      });
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  showDialog(index: number) {
    this.index = index;
    this.dialogVisible = true;
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractInputForCalculation = () => this.loonUitBuitenland.getRawValue().map(item => ({
      bedrag: item.loon.bedrag,
      loonheffing: item.loonheffing,
      buitenlandseLoonheffing: item.buitenlandseLoonheffing,
      afdrachtZVWinNLmoetnog: item.afdrachtZVWinNLmoetnog,
      inkomenZitInNederlandseGrondslag: item.inkomenZitInNederlandseGrondslag,
      loonUitTegenwoordigeBuitenlandseDienstbetrekking: item.loonUitTegenwoordigeBuitenlandseDienstbetrekking,
      pasVerrekenMethodeTo2: item.pasVerrekenMethodeToe,
    }));

    this.changeSubscriptions.add(
      this.loonUitBuitenland.valueChanges.pipe(
        debounceTime(500),
        map(() => this.saveForm()),
        map(() => extractInputForCalculation()),
        startWith(extractInputForCalculation()),
        pairwise(),
        filter(([prev, curr]) => JSON.stringify(prev) !== JSON.stringify(curr)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.dataService.calculateLatestIncomeTaxReturn().subscribe();
      })
    );
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.formGroup.reset({}, {emitEvent: false}); // Prevent reset from emitting events
    this.updateForm(details.box1?.belastbaarLoon?.loonUitBuitenlandseDienstbetrekkingOfPensioen || []);
  }

  get loonUitBuitenland(): FormArray<LoonFormGroup> {
    return this.formGroup.controls.loonUitBuitenland;
  }

  addRij(): void {
    this.loonUitBuitenland.push(this.createFormGroup(), { emitEvent: false });
  }

  verwijderRij(index: number): void {
    this.loonUitBuitenland.removeAt(index, { emitEvent: false });
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(item?: LoonBuitenland): FormGroup {
    return new FormGroup({
      loon: new FormGroup({
        omschrijving: new FormControl<string | null>(item?.loon?.omschrijving ?? null),
        bedrag: new FormControl<number | null>(item?.loon?.bedrag ?? null),
      }),
      loonheffing: new FormControl<number | null>(item?.loonheffing ?? null),
      loonsoort: new FormControl<string | null>(item?.loonsoort ?? null),
      landCode: new FormControl<string | null>(item?.landCode ?? null),
      loonUitTegenwoordigeBuitenlandseDienstbetrekking: new FormControl<boolean | null>(item?.loonUitTegenwoordigeBuitenlandseDienstbetrekking ?? false),
      belastDeelLoonInNederland: new FormControl<number | null>(item?.loon?.bedrag ?? null),
      buitenlandseLoonheffing: new FormControl<number | null>(item?.buitenlandseLoonheffing ?? null),
      inkomenZitInNederlandseGrondslag: new FormControl<boolean | null>(item?.inkomenZitInNederlandseGrondslag ?? false),
      pasVerrekenMethodeToe: new FormControl<boolean | null>(item?.pasVerrekenMethodeToe ?? true),
      afdrachtZVWinNLmoetnog: new FormControl<boolean | null>(item?.afdrachtZVWinNLmoetnog ?? false),
      benutAlgemeneCompensatieregeling: new FormControl<boolean | null>(item?.benutAlgemeneCompensatieregeling ?? false),
      benutBijzondereCompensatieregeling: new FormControl<boolean | null>(item?.benutBijzondereCompensatieregeling ?? false),
      compensatieregelingDuitsland: new FormControl<boolean | null>(item?.compensatieregelingDuitsland ?? false),
      veranderingVanWerkgever: new FormControl<boolean | null>(item?.veranderingVanWerkgever ?? false),
      inBelgieOfDuitslandIngehoudenBelasting: new FormControl<number | null>(item?.inBelgieOfDuitslandIngehoudenBelasting ?? null),
    })
  }

  private updateForm(loon: LoonBuitenland[]): void {
    let lijst = loon || [];
    const formArray = this.loonUitBuitenland;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    this.formGroup.updateValueAndValidity({emitEvent: false});

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      const updateLoon = (aangifte: Aangifte) => ({
        ...aangifte,
        box1: {
          ...aangifte.box1,
          belastbaarLoon: {
            ...aangifte.box1?.belastbaarLoon,
            loonUitBuitenlandseDienstbetrekkingOfPensioen: this.formGroup.getRawValue().loonUitBuitenland,
          },
        },
      }) as Aangifte;

      const updatedAangifte = this.isPartner ? {
        ...currentEntity.aangifte,
        partnerAangifte: updateLoon(currentEntity.aangifte.partnerAangifte!),
      } : updateLoon(currentEntity.aangifte);

      return {
        ...currentEntity,
        aangifte: updatedAangifte,
      } as IncomeTaxEntity;
    });
  }
}
